'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { RHFShadcnTextField } from '@/components/RHF';
import FormProvider from '@/components/RHF/FormProvider';
import { Button } from '@/components/ui/button';
import { FormValidationSummary } from '@/components/ui/form-validation-summary';
import { Icon } from '@/components/ui/icon';
import { toast } from '@/components/ui/use-toast';
import { frontEndRoutes } from '@/constants/routes';
import { AuthLayout } from '@/containers/layouts/AuthLayout';
import { AuthTextLink } from '@/containers/screens/page-auth-shared/AuthTextLink';
import { getFirebaseAuthErrorMessage } from '@/containers/screens/page-auth-shared/getFirebaseAuthErrorMessage';
import { sendPasswordResetLink } from '@/services/firebaseServices/AuthServices/authentication';

type ForgotPasswordFormFields = {
  email: string;
};

/**
 * Maps Firebase password-reset error codes to the forgot-password translation
 * keys. `auth/user-not-found` is intentionally not surfaced. Firebase
 * suppresses it to avoid account enumeration, so the success view is shown
 * either way. Codes not listed here fall back to
 * `auth.forgotPassword.errorSendingEmail`.
 */
const RESET_ERROR_KEYS: Record<string, string> = {
  'auth/invalid-email': 'auth.forgotPassword.invalidEmail',
  'auth/user-disabled': 'auth.forgotPassword.userDisabled',
  'auth/too-many-requests': 'auth.forgotPassword.tooManyRequests',
  'auth/network-request-failed': 'auth.forgotPassword.networkError',
};

function getResetErrorMessage(errorCode: string, translate: (key: string) => string): string {
  return getFirebaseAuthErrorMessage(errorCode, translate, {
    codeToKey: RESET_ERROR_KEYS,
    defaultKey: 'auth.forgotPassword.errorSendingEmail',
  });
}

export function ForgotPasswordPage() {
  const translate = useTranslations();
  const router = useRouter();
  const [submittedEmail, setSubmittedEmail] = useState('');

  const forgotPasswordSchema = z.object({
    email: z
      .string()
      .email(translate('auth.validation.emailInvalid')),
  });

  const methods = useForm<ForgotPasswordFormFields>({
    mode: 'onChange',
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const { handleSubmit, formState: { isSubmitting } } = methods;

  const onSubmit = async (data: ForgotPasswordFormFields) => {
    setSubmittedEmail('');
    try {
      await sendPasswordResetLink(data.email);
    } catch (error: unknown) {
      const errorCode = (error as { code?: string })?.code ?? '';
      toast({
        title: translate('auth.forgotPassword.errorSendingEmail'),
        description: getResetErrorMessage(errorCode, translate),
        variant: 'error' as const,
      });
      return;
    }
    setSubmittedEmail(data.email);
  };

  const hasSuccess = submittedEmail.length > 0;

  // Auto-redirect to sign-in 5s after a successful send. Stored + cleared so
  // it can't fire after the user follows the "back to sign in" link or the
  // component unmounts (was a leaking, uncancelled timer).
  useEffect(() => {
    if (hasSuccess === false) {
      return;
    }
    const timer = setTimeout(() => {
      router.push(frontEndRoutes.auth.signIn);
    }, 5000);
    return () => clearTimeout(timer);
  }, [hasSuccess, router]);

  return (
    <AuthLayout>
      <div className="flex flex-col gap-6">
        {hasSuccess === true
          ? (
              <section
                role="status"
                aria-live="polite"
                className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-2 duration-[var(--duration-medium-2)] ease-[var(--ease-emphasized)] motion-reduce:animate-none"
              >
                <span className="grid size-12 place-items-center rounded-full bg-success-container text-on-success-container">
                  <Icon
                    icon="lucide:circle-check"
                    size={24}
                    aria-hidden="true"
                  />
                </span>
                <header className="flex flex-col gap-2">
                  <h1 className="type-headline-m text-foreground">
                    {translate('auth.forgotPassword.successHeadline')}
                  </h1>
                  <p className="type-body-l text-on-surface-variant text-balance">
                    {translate('auth.forgotPassword.successDescription', { email: submittedEmail })}
                  </p>
                </header>
              </section>
            )
          : (
              <>
                <header className="flex flex-col gap-2">
                  <h1 className="type-headline-m md:type-headline-l text-foreground">
                    {translate('auth.forgotPassword.title')}
                  </h1>
                  <p className="type-body-l text-on-surface-variant text-balance">
                    {translate('auth.forgotPassword.subtitle')}
                  </p>
                </header>

                <FormProvider methods={methods}>
                  <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    <RHFShadcnTextField
                      name="email"
                      title={translate('auth.forgotPassword.emailLabel')}
                      fieldType="email"
                      autoComplete="email"
                      disabled={isSubmitting === true}
                    />

                    <FormValidationSummary
                      heading={translate('common.form.fixTheFollowing')}
                      fieldLabels={{
                        email: translate('auth.forgotPassword.emailLabel'),
                      }}
                    />

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full"
                      loading={isSubmitting === true}
                      disabled={isSubmitting === true}
                    >
                      {translate('auth.forgotPassword.submit')}
                    </Button>
                  </form>
                </FormProvider>
              </>
            )}

        <footer className="flex justify-center border-t border-outline-variant pt-6">
          <AuthTextLink
            href={frontEndRoutes.auth.signIn}
            className="inline-flex items-center gap-2 type-body-m text-secondary"
            icon="lucide:arrow-left"
          >
            {translate('auth.forgotPassword.backToSignIn')}
          </AuthTextLink>
        </footer>
      </div>
    </AuthLayout>
  );
}
